<template>
	<div class="tcp-page tcp-shop">
		<section class="tcp-shop__wrap">
			<nav class="tcp-crumbs">
				<router-link to="/">Home</router-link>
				<span>/</span>
				<span v-if="!activeCategory">Shop</span>
				<template v-else>
					<router-link to="/shop">Shop</router-link>
					<span>/</span>
					<span>{{ activeCategory.name }}</span>
				</template>
			</nav>

			<header class="tcp-shop__head">
				<h1>{{ title }}</h1>
				<p>{{ lead }}</p>
			</header>

			<div class="tcp-filters" v-if="categories.length">
				<button type="button" :class="{ 'is-active': !activeCategory }" @click="setCategory(null)">All Products</button>
				<button
					v-for="category in categories"
					:key="category.id"
					type="button"
					:class="{ 'is-active': activeCategory && activeCategory.id === category.id }"
					@click="setCategory(category)"
				>{{ category.name }}</button>
			</div>

			<div class="tcp-shop__bar">
				<p>{{ filtered.length }} product{{ filtered.length === 1 ? '' : 's' }}</p>
				<div class="tcp-shop__tools">
					<label>
						<span>Sort by</span>
						<select :value="sort" @change="setSort($event.target.value)">
							<option value="featured">Featured</option>
							<option value="price-low">Price: Low to High</option>
							<option value="price-high">Price: High to Low</option>
							<option value="newest">Newest</option>
							<option value="best">Best Selling</option>
						</select>
					</label>
					<div class="tcp-shop__views">
						<button type="button" :class="{ 'is-active': viewMode === 'grid' }" aria-label="Grid" @click="viewMode = 'grid'"><i class="fa fa-th"></i></button>
						<button type="button" :class="{ 'is-active': viewMode === 'list' }" aria-label="List" @click="viewMode = 'list'"><i class="fa fa-list"></i></button>
					</div>
				</div>
			</div>

			<div v-if="filtered.length" class="tcp-shop__grid" :class="{ 'is-list': viewMode === 'list' }">
				<article v-for="product in filtered" :key="product.id" class="tcp-shop-card">
					<router-link :to="'/product/' + product.id" class="tcp-shop-card__media">
						<img v-if="product.image_url" :src="product.image_url" :alt="product.name">
						<span v-else class="tcp-card__placeholder"><i class="fa fa-box"></i></span>
						<span v-if="!isAvailable(product)" class="tcp-shop-card__badge">Sold out</span>
					</router-link>
					<div class="tcp-shop-card__body">
						<p v-if="product.presentation" class="tcp-shop-card__kicker">{{ product.presentation }}</p>
						<router-link :to="'/product/' + product.id">{{ product.name }}</router-link>
						<strong>{{ money(product.price) }}</strong>
						<p v-if="viewMode === 'list' && product.excerpt" class="tcp-shop-card__excerpt">{{ product.excerpt }}</p>
						<button type="button" class="tcp-btn tcp-btn--small" :disabled="!isAvailable(product)" @click="add(product)">
							{{ isAvailable(product) ? 'Add to cart' : 'Sold out' }}
						</button>
					</div>
				</article>
			</div>
			<p v-else-if="!loading" class="tcp-empty">No products match this view.</p>
		</section>
	</div>
</template>

<script>
export default {
	data() {
		return {
			products: [],
			categories: [],
			term: '',
			viewMode: 'grid',
			loading: true
		}
	},
	computed: {
		sort() {
			return this.$route.query.sort || 'featured'
		},
		activeCategory() {
			const slug = this.$route.query.category
			if (!slug) return null
			return this.categories.find(category => category.slug === slug || String(category.id) === String(slug)) || null
		},
		title() {
			if (this.term) return 'Search'
			if (this.sort === 'newest') return 'New Products'
			if (this.sort === 'best') return 'Best Sellers'
			if (this.activeCategory) return this.activeCategory.name
			return 'All Products'
		},
		lead() {
			if (this.term) return 'Results for “' + this.term + '”.'
			if (this.sort === 'newest') return 'The latest additions to the pantry.'
			if (this.sort === 'best') return 'The products customers reach for most.'
			if (this.activeCategory && this.activeCategory.description) return this.activeCategory.description
			return 'Essential supplements and wellness products for your daily routine.'
		},
		filtered() {
			const term = this.term.trim().toLowerCase()
			const rows = this.products.filter(product => {
				const matchesCategory = !this.activeCategory || (product.categories || []).some(category => category.id === this.activeCategory.id)
				const matchesSearch = !term || [product.name, product.presentation, product.excerpt].some(value => value && value.toLowerCase().includes(term))
				return matchesCategory && matchesSearch
			})
			const sorted = rows.slice()
			if (this.sort === 'price-low') sorted.sort((a, b) => Number(a.price) - Number(b.price))
			else if (this.sort === 'price-high') sorted.sort((a, b) => Number(b.price) - Number(a.price))
			else if (this.sort === 'newest') sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
			else if (this.sort === 'best') sorted.sort((a, b) => Number(b.sold || 0) - Number(a.sold || 0))
			else sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
			return sorted
		}
	},
	watch: {
		'$route.query.q': {
			immediate: true,
			handler(value) {
				this.term = value || ''
			}
		}
	},
	mounted() {
		axios.get(tools.url('/api/store/categories')).then((response) => {
			this.categories = response.data || []
		}).catch(() => {})

		axios.get(tools.url('/api/store/products')).then((response) => {
			this.products = response.data || []
			this.loading = false
		}).catch(() => {
			this.loading = false
		})
	},
	methods: {
		money(value) {
			return '$' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
		},
		isAvailable(product) {
			return Number(product.stock) > 0 && product.status !== 'Agotado'
		},
		setCategory(category) {
			const query = Object.assign({}, this.$route.query)
			if (!category) delete query.category
			else query.category = category.slug || category.id
			this.$router.push({ path: '/shop', query: query })
		},
		setSort(value) {
			const query = Object.assign({}, this.$route.query)
			if (!value || value === 'featured') delete query.sort
			else query.sort = value
			this.$router.push({ path: '/shop', query: query })
		},
		add(product) {
			if (!this.isAvailable(product)) return
			this.$cart.add(product, 1)
		}
	}
}
</script>
